import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { ArrowRight, MapPin, Phone, Clock, Star, ChefHat, Truck, QrCode, FileText } from 'lucide-react';
import PublicLayout from '../../components/shared/PublicLayout';
import axios from 'axios';
import heroImage from '../../assets/restaurant-buffet-accueil.jpeg';
import reservationImage from '../../assets/reserver-votre-table-terrasse.jpeg';
import { getApiBase } from '../../lib/api';
import { getPublicImageUrl } from '../../lib/media';

const API = getApiBase();

export default function HomePage() {
  const [dishes, setDishes] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/dishes/popular?limit=6`).then(r => setDishes(r.data || [])).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative flex min-h-[560px] items-center sm:min-h-[640px] lg:h-[85vh]" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Comptoir et buffet du restaurant Le Traiteur Libanais"
            className="h-full w-full object-cover object-[58%_center] sm:object-[60%_center] lg:object-center"
          />
          <div className="absolute inset-0 bg-[linear-gradient(118deg,rgba(10,14,20,0.9)_0%,rgba(10,14,20,0.64)_34%,rgba(10,14,20,0.22)_72%,rgba(10,14,20,0.1)_100%)]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl rounded-[32px] border border-white/10 bg-[#10161F]/20 px-5 py-8 shadow-[0_24px_80px_rgba(0,0,0,0.18)] backdrop-blur-[2px] sm:px-8 sm:py-10">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#D4A840] font-medium mb-4 animate-fade-in">Restaurant Libanais Authentique</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-[1.05] tracking-tighter mb-6 animate-slide-up">
              Le Traiteur<br />Libanais
            </h1>
            <p className="mb-8 max-w-lg text-base leading-relaxed !text-white sm:text-lg [text-shadow:0_2px_12px_rgba(0,0,0,0.55)]" style={{ color: '#ffffff', opacity: 1 }}>
              L'authenticite libanaise au coeur de Bagnolet. Savourez nos plats prepares avec passion et les meilleurs ingredients du Levant.
            </p>
            <div className="animate-slide-up stagger-2">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <Link to="/reservation" className="w-full">
                  <Button className="h-12 w-full rounded-[18px] bg-[#D4A840] px-5 text-[#1A2332] hover:bg-[#c9a030] font-medium" data-testid="hero-reserve-btn">
                    Reserver une table <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/commander" className="w-full">
                  <Button variant="outline" className="h-12 w-full rounded-[18px] border-white/70 bg-transparent px-5 text-white hover:bg-white hover:text-[#1A2332] font-medium" data-testid="hero-order-btn">
                    Commander en ligne
                  </Button>
                </Link>
                <Link to="/devis" className="w-full sm:col-span-2 xl:col-span-1">
                  <Button variant="outline" className="h-12 w-full rounded-[18px] border-[#D4A840]/60 bg-[#D4A840]/12 px-5 text-white hover:border-[#D4A840] hover:bg-[#D4A840] hover:text-[#1A2332] font-medium" data-testid="hero-quote-btn">
                    <FileText className="mr-2 w-4 h-4" /> Demander un devis
                  </Button>
                </Link>
              </div>
              <p className="mt-4 text-sm text-white/82">
                Evenements, buffets, repas de groupe ou besoin professionnel : demandez un devis rapide en ligne.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: ChefHat, title: 'Cuisine Authentique', desc: 'Des recettes traditionnelles libanaises preparees avec passion par nos chefs.' },
              { icon: Truck, title: 'Livraison & Emporter', desc: 'Commandez en ligne et recevez vos plats chez vous ou recuperez-les au restaurant.' },
              { icon: QrCode, title: 'Commande sur place', desc: 'Scannez le QR code sur votre table pour commander directement depuis votre telephone.' },
            ].map((f, i) => (
              <div key={i} className={`bg-white p-8 rounded-2xl border border-[#EBE8E0] shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all animate-slide-up stagger-${i+1}`}>
                <f.icon className="w-8 h-8 text-[#5B6B3F] mb-4" />
                <h3 className="font-heading text-xl font-semibold text-[#2A2A28] mb-2">{f.title}</h3>
                <p className="text-sm text-[#7A7A75] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Preview */}
      {dishes.length > 0 && (
        <section className="py-24 sm:py-32 bg-white" data-testid="menu-preview-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-medium mb-3">Decouvrez</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#2A2A28] tracking-tight">Nos plats les plus commandes</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {dishes.map((dish, i) => (
                <div key={dish.id} className={`dish-card bg-[#FAF8F5] rounded-2xl overflow-hidden border border-[#EBE8E0] animate-slide-up stagger-${(i%3)+1}`}>
                  {dish.image && (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={getPublicImageUrl(dish.image)} alt={dish.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-semibold text-[#2A2A28] mb-1">{dish.name}</h3>
                    <p className="text-sm text-[#7A7A75] mb-3 line-clamp-2">{dish.description}</p>
                    <span className="text-[#5B6B3F] font-semibold">A partir de {Math.min(dish.prices?.dine_in || 0, dish.prices?.delivery || 99, dish.prices?.takeaway || 99).toFixed(2)} EUR</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link to="/commander">
                <Button className="rounded-full bg-[#5B6B3F] hover:bg-[#4A5832] text-white px-8 h-11" data-testid="see-full-menu-btn">
                  Commander maintenant <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Contact */}
      <section className="py-24 sm:py-32" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-medium mb-3">Nous trouver</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#2A2A28] tracking-tight mb-6">Venez nous rendre visite</h2>
              <div className="space-y-4">
                {[
                  { icon: MapPin, text: '16 Rue Edouard Vaillant, 93170 Bagnolet' },
                  { icon: Phone, text: 'Tel : 09 50 00 05 41' },
                  { icon: Clock, text: 'Lun-Ven 11h30-14h30 / 18h30-22h30' },
                  { icon: Star, text: '482 Avis Google - 4,4/5' },
                ].map((c, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <c.icon className="w-5 h-5 text-[#5B6B3F] flex-shrink-0" />
                    <span className="text-[#2A2A28]">{c.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-[#EBE8E0] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <img
                src={reservationImage}
                alt="Terrasse du restaurant Le Traiteur Libanais"
                className="h-64 w-full object-cover object-center"
              />
              <div className="p-6 text-center">
                <h3 className="font-heading text-xl font-semibold text-[#2A2A28] mb-3">Reservez votre table</h3>
                <p className="mb-5 text-sm text-[#7A7A75]">Pour une table, une commande ou un evenement, choisissez l'action adaptee en un clic.</p>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Link to="/reservation">
                    <Button className="w-full rounded-full bg-[#5B6B3F] hover:bg-[#4A5832] text-white px-5" data-testid="footer-reserve-btn">
                      Reserver
                    </Button>
                  </Link>
                  <Link to="/commander">
                    <Button variant="outline" className="w-full rounded-full border-[#5B6B3F]/20 text-[#5B6B3F] hover:bg-[#5B6B3F]/5 px-5" data-testid="footer-order-btn">
                      Commander
                    </Button>
                  </Link>
                  <Link to="/devis">
                    <Button variant="outline" className="w-full rounded-full border-[#D4A840]/40 text-[#A77A10] hover:bg-[#D4A840]/10 px-5" data-testid="footer-quote-btn">
                      Devis
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
